import { useMemo } from "react"
import { DictType } from "@pma/types"
import useTranslator from "@pma/hooks/use-translator"
import { computeBillTotal, cn } from "@pma/utils/helpers"
import { formatCurrency } from "@pma/utils/utils"
import { Card, CardContent, CardHeader } from "@components/common/card"

interface Props {
  bill: DictType
  previousBills?: DictType[]
  retentionPercent?: number
  vatPercent?: number
}

export function AccumulatedBillBreakdown({ bill, previousBills = [], retentionPercent = 0, vatPercent = 18 }: Props) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const breakdown = useMemo(() => {
    const previousAccumulated = previousBills.reduce(
      (sum, b) => sum + computeBillTotal(b, false),
      0,
    )
    const currentBillTotal = computeBillTotal(bill, false)
    const newTotal = previousAccumulated + currentBillTotal
    const retention = currentBillTotal * (retentionPercent / 100)
    const netToPay = currentBillTotal - retention
    const vat = netToPay * (vatPercent / 100)
    const toPay = netToPay + vat

    return {
      previousAccumulated,
      currentBillTotal,
      newTotal,
      retention,
      vat,
      toPay,
    }
  }, [bill, previousBills, retentionPercent, vatPercent])

  return (
    <Card
      className={cn("mt-4 border-slate-200", isHebrew ? "rtl" : "ltr")}
      dir={isHebrew ? "rtl" : "ltr"}
    >
      <CardContent className='px-0 pb-0 pt-0'>
        <table className='w-full text-sm'>
          <tbody>
            {/* Total price */}
            <tr className='border-b border-slate-100'>
              <td className='px-4 py-2 text-muted-foreground'>{translate("total price")}</td>
              <td className='px-4 py-2 text-end font-medium'>
                ₪ {formatCurrency(breakdown.currentBillTotal, isHebrew)}
              </td>
            </tr>

            {/* Retention */}
            {retentionPercent > 0 && (
              <tr className='border-b border-slate-100'>
                <td className='px-4 py-2 text-muted-foreground'>
                  {translate("retention")} {retentionPercent}%
                </td>
                <td className='px-4 py-2 text-end font-medium text-red-600'>
                  ₪ {formatCurrency(breakdown.retention, isHebrew)}
                </td>
              </tr>
            )}

            {/* Net to pay (before VAT) */}
            <tr className='border-b border-slate-100'>
              <td className='px-4 py-2 text-muted-foreground'>{translate("net to pay")}</td>
              <td className='px-4 py-2 text-end font-medium'>
                ₪ {formatCurrency(breakdown.currentBillTotal - breakdown.retention, isHebrew)}
              </td>
            </tr>

            {/* VAT */}
            <tr className='border-b border-slate-200'>
              <td className='px-4 py-2 text-muted-foreground'>
                {translate("vat")} {vatPercent}%
              </td>
              <td className='px-4 py-2 text-end font-medium'>
                ₪ {formatCurrency(breakdown.vat, isHebrew)}
              </td>
            </tr>

            {/* Total to pay including VAT */}
            <tr className='bg-slate-50'>
              <td className='px-4 py-3 text-base font-semibold'>
                {translate("total to pay including vat")}
              </td>
              <td className='px-4 py-3 text-end text-base font-bold text-primary'>
                ₪ {formatCurrency(breakdown.toPay, isHebrew)}
              </td>
            </tr>
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}
